import useFetch from '../../hooks/useFetch'
import useLocale from '../../hooks/useLocale'
import useRoutesConfig from '../../hooks/useRoutesConfig'
import useTheme from '../../hooks/useTheme'
import { ROUTE_FILTER_OPTIONS } from '../../utils/routeClassifier'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MenuItem, Select, TextField } from '@mui/material'
import LobbiesList from './LobbiesList'
import LobbyInfo from '../../common/LobbyInfo'
import { urlApi } from '../../config'
import { DateRangePicker } from 'mui-daterange-picker'
import Modal from 'react-modal'
import LoadingIndicator from '../../common/LoadingIndicator'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faUserClock,
  faCalendarDay,
  faArrowUpWideShort,
  faTaxi,
  faCalendarDays,
  faXmark,
  faMapMarkerAlt,
  faChevronDown,
  faChevronUp,
  faFilter
} from '@fortawesome/free-solid-svg-icons'

const Lobbies = ({ selectedLobby, setSelectedLobby, hidden, openSearchModal, setOpenSearchModal }) => {
  const locale = useLocale()
  const routesConfig = useRoutesConfig()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const { loading, data } = useFetch(`${urlApi}/admin/lobbies${locale ? `?locale=${locale}` : ''}`, {}, true)
  const [filters, setFilters] = useState(['active'])
  const [lobbyFilter, setLobbyFilter] = useState('')
  const [routeFilter, setRouteFilter] = useState('all')
  const [originSearch, setOriginSearch] = useState('')
  const [destinationSearch, setDestinationSearch] = useState('')
  const [nameSearch, setNameSearch] = useState('')
  const [dateRange, setDateRange] = useState({})
  const [openDatePicker, setOpenDatePicker] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchError, setSearchError] = useState('')
  const [searchLobbyId, setSearchLobbyId] = useState('')
  const [fields, setFields] = useState({
    filters,
    originSearch,
    destinationSearch,
    nameSearch,
    dateRange,
    routeFilter
  }) //Contendra todos los filtros dentro
  const [length, setLength] = useState('?')
  const [headerCollapsed, setHeaderCollapsed] = useState(true)
  const isMobile = window.innerWidth < 640

  useEffect(() => {
    setFields({
      filters,
      lobbyFilter,
      nameSearch,
      originSearch,
      destinationSearch,
      dateRange,
      routeFilter
    })
  }, [filters, originSearch, destinationSearch, nameSearch, dateRange, lobbyFilter, routeFilter])

  useEffect(() => {
    if (!openSearchModal) {
      setSearchInput('')
      setSearchError('')
    }
  }, [openSearchModal])

  // Deep-link: abrir un viaje directo por ?lobby_id= (lo usa el panel En Vivo).
  // Lobbies se monta una sola vez (vive fuera de <Routes>), así que reaccionamos
  // al cambio de la query — no sólo al montar.
  const location = useLocation()
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('lobby_id')
    if (id) setSearchLobbyId(id)
  }, [location.search])

  const toggleFilter = filter => {
    if (filters.includes(filter)) {
      setFilters(filters.filter(f => f !== filter))
    } else {
      setFilters([...filters, filter])
    }
  }

  const filterOptions = [
    { value: 'active', label: 'Viajes nuevos', icon: faCalendarDay, color: 'emerald' },
    { value: 'company', label: 'Con acompañantes', icon: faUsers, color: 'blue' },
    { value: 'pending-companions', label: 'Acompañantes pendientes', icon: faUserClock, color: 'amber' },
    { value: 'choferes', label: 'Choferes', icon: faTaxi, color: 'yellow' }
  ]

  const getFilterStyles = (color, isActive) => {
    const colors = {
      blue: isActive
        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
        : 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-500/20',
      amber: isActive
        ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
        : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-500/20',
      slate: isActive
        ? 'bg-slate-600 text-white shadow-lg shadow-slate-500/30'
        : 'bg-slate-100 dark:bg-dk-raised text-slate-600 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-dk-hover',
      emerald: isActive
        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
        : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-500/20',
      violet: isActive
        ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/30'
        : 'bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-500/20',
      yellow: isActive
        ? 'bg-[#FDB813] text-white shadow-lg shadow-yellow-500/30'
        : 'bg-yellow-50 dark:bg-amber-500/10 text-yellow-700 dark:text-amber-400 hover:bg-yellow-100 dark:hover:bg-amber-500/20'
    }
    return colors[color]
  }

  const handleSearchLobby = () => {
    if (!searchInput.trim()) {
      setSearchError('Por favor ingresa un ID de lobby')
      return
    }

    setSearchError('')
    setSearchLobbyId(searchInput)
    setOpenSearchModal(false)
    setSearchInput('')
  }

  return (
    <div className='w-full h-full overflow-auto' hidden={hidden}>
      {/* Header fijo con filtros y búsqueda */}
      <div className='sticky top-3 z-20 mx-3 sm:mx-4 bg-white/95 dark:bg-dk-surface/95 backdrop-blur-sm px-4 sm:px-5 pt-3 pb-2 rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.05)]'>
        {/* Filtros principales - Pills */}
        <div className='flex flex-wrap gap-2 mb-3'>
          {filterOptions.map(option => (
            <button
              key={option.value}
              onClick={() => toggleFilter(option.value)}
              className={`
                inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-sm font-medium
                transition-all duration-200 cursor-pointer border-0
                ${getFilterStyles(option.color, filters.includes(option.value))}
              `}
            >
              <FontAwesomeIcon icon={option.icon} className='text-xs' />
              <span>{option.label}</span>
            </button>
          ))}
        </div>

        {/* Botón para expandir/colapsar en mobile */}
        <button
          onClick={() => setHeaderCollapsed(!headerCollapsed)}
          className='sm:hidden w-full flex items-center justify-center gap-2 py-2 mb-2 bg-slate-100 dark:bg-dk-raised hover:bg-slate-200 dark:hover:bg-dk-hover rounded-xl text-slate-600 dark:text-gray-300 text-sm font-medium transition-all duration-200'
        >
          <FontAwesomeIcon icon={faFilter} className='text-xs' />
          <span>{headerCollapsed ? 'Mostrar filtros' : 'Ocultar filtros'}</span>
          <FontAwesomeIcon icon={headerCollapsed ? faChevronDown : faChevronUp} className='text-xs' />
        </button>

        {/* Búsqueda y filtros secundarios - Colapsable en mobile */}
        <div className={`${isMobile && headerCollapsed ? 'hidden' : 'block'} sm:block bg-slate-50/90 dark:bg-dk-bg/90 backdrop-blur-sm rounded-2xl p-3 mb-2 border border-slate-100 dark:border-dk-border transition-all duration-300`}>
        {/* Campos de búsqueda */}
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3'>
          <TextField
            size='small'
            label='Usuario / Id Lobby'
            onChange={evt => setNameSearch(evt.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isDark ? '#1f242b' : 'white',
                '&:hover fieldset': { borderColor: '#d65944' },
                '&.Mui-focused fieldset': { borderColor: '#d65944' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#d65944' }
            }}
          />
          <TextField
            size='small'
            label='Origen'
            onChange={evt => setOriginSearch(evt.target.value)}
            InputProps={{
              startAdornment: (
                <FontAwesomeIcon icon={faMapMarkerAlt} className='text-slate-400 dark:text-gray-400 mr-2 text-sm' />
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isDark ? '#1f242b' : 'white',
                '&:hover fieldset': { borderColor: '#d65944' },
                '&.Mui-focused fieldset': { borderColor: '#d65944' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#d65944' }
            }}
          />
          <TextField
            size='small'
            label='Destino'
            onChange={evt => setDestinationSearch(evt.target.value)}
            InputProps={{
              startAdornment: (
                <FontAwesomeIcon icon={faMapMarkerAlt} className='text-slate-400 dark:text-gray-400 mr-2 text-sm' />
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: isDark ? '#1f242b' : 'white',
                '&:hover fieldset': { borderColor: '#d65944' },
                '&.Mui-focused fieldset': { borderColor: '#d65944' }
              },
              '& .MuiInputLabel-root.Mui-focused': { color: '#d65944' }
            }}
          />
        </div>

        {/* Filtros de estado y fecha */}
        <div className='flex flex-wrap items-center gap-2'>
          <Select
            defaultValue='none'
            size='small'
            onChange={evt => setLobbyFilter(evt.target.value)}
            sx={{
              minWidth: 150,
              borderRadius: '12px',
              backgroundColor: isDark ? '#1f242b' : 'white',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d65944' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#d65944' }
            }}
          >
            <MenuItem value={'none'}>
              <span className='text-slate-400 dark:text-gray-400'>Estado</span>
            </MenuItem>
            <MenuItem value={'pending'}>
              <span className='flex items-center gap-2'>
                <span className='w-2 h-2 rounded-full bg-amber-400' />
                Pendiente
              </span>
            </MenuItem>
            <MenuItem value={'completed'}>
              <span className='flex items-center gap-2'>
                <span className='w-2 h-2 rounded-full bg-emerald-500' />
                Completado
              </span>
            </MenuItem>
            <MenuItem value={'cancelled'}>
              <span className='flex items-center gap-2'>
                <span className='w-2 h-2 rounded-full bg-red-500' />
                Cancelado
              </span>
            </MenuItem>
            <MenuItem value={'not-cancelled'}>
              <span className='flex items-center gap-2'>
                <span className='w-2 h-2 rounded-full bg-slate-400 dark:bg-dk-hover' />
                No cancelados
              </span>
            </MenuItem>
          </Select>

          <Select
            value={routeFilter}
            size='small'
            onChange={evt => setRouteFilter(evt.target.value)}
            sx={{
              minWidth: 150,
              borderRadius: '12px',
              backgroundColor: isDark ? '#1f242b' : 'white',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d65944' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#d65944' }
            }}
          >
            {ROUTE_FILTER_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.value === 'all' ? <span className='text-slate-400 dark:text-gray-400'>Ruta</span> : option.label}
              </MenuItem>
            ))}
          </Select>

          <button
            className={`
              inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium
              transition-all duration-200 border
              ${
                dateRange?.startDate
                  ? 'bg-primary text-white border-primary shadow-md'
                  : 'bg-white dark:bg-dk-surface text-slate-600 dark:text-gray-300 border-slate-200 dark:border-dk-border hover:border-primary hover:text-primary'
              }
            `}
            onClick={() => setOpenDatePicker(!openDatePicker)}
          >
            <FontAwesomeIcon icon={faCalendarDays} />
            {dateRange?.startDate ? (
              <span>
                {dateRange.startDate.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}
                {' - '}
                {dateRange.endDate.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}
              </span>
            ) : (
              <span>Fechas</span>
            )}
          </button>

          {dateRange?.startDate && (
            <button
              className='w-8 h-8 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-500/10 text-red-500 dark:text-red-400 hover:bg-red-500 hover:text-white transition-all duration-200'
              onClick={() => setDateRange({})}
            >
              <FontAwesomeIcon icon={faXmark} className='text-sm' />
            </button>
          )}

          <button
            onClick={() => toggleFilter('orderByTime')}
            className={`
              inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium
              transition-all duration-200 border
              ${
                filters.includes('orderByTime')
                  ? 'bg-violet-500 text-white border-violet-500 shadow-md'
                  : 'bg-white dark:bg-dk-surface text-slate-600 dark:text-gray-300 border-slate-200 dark:border-dk-border hover:border-violet-500 hover:text-violet-500'
              }
            `}
          >
            <FontAwesomeIcon icon={faArrowUpWideShort} />
            <span>Por tiempo</span>
          </button>
        </div>
        </div>

        {/* Total de viajes */}
        <div className='flex items-center gap-2 pb-2'>
          <div className='bg-gradient-to-r from-primary to-light_coral text-white px-3 py-1 rounded-lg font-bold text-sm shadow-sm'>
            {length}
          </div>
          <span className='text-slate-500 dark:text-gray-400 text-sm font-medium'>viajes encontrados</span>
        </div>
      </div>

      <div className='relative z-50'>
        <DateRangePicker
          open={openDatePicker}
          toggle={() => setOpenDatePicker(!openDatePicker)}
          onChange={range => {
            setDateRange(range)
            setOpenDatePicker(!openDatePicker)
          }}
        />
      </div>

      {/* Lista de lobbies */}
      <div className='mt-6 px-3 sm:px-4'>
        {loading ? (
          <div className='py-20 flex items-center justify-center'>
            <LoadingIndicator />
          </div>
        ) : (
          <LobbiesList
            data={data}
            setSelectedLobby={setSelectedLobby}
            fields={fields}
            setLength={setLength}
            routesConfig={routesConfig}
          />
        )}
      </div>
      {Object.keys(selectedLobby).length != 0 ? (
        <PreLobbyInfo lobbyId={selectedLobby._id} setSelectedLobby={setSelectedLobby} />
      ) : (
        <></>
      )}
      {searchLobbyId && (
        <SearchLobbyInfo
          lobbyId={searchLobbyId}
          setSelectedLobby={setSelectedLobby}
          setSearchLobbyId={setSearchLobbyId}
        />
      )}

      <Modal
        isOpen={openSearchModal}
        onRequestClose={() => setOpenSearchModal(false)}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000
          },
          content: {
            width: '400px',
            height: '300px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 'auto',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: '#ffffff',
            padding: '30px'
          }
        }}
      >
        <h2 className='text-xl font-semibold mb-6 text-gray-800 dark:text-gray-100'>Búsqueda por ID</h2>
        <TextField
          fullWidth
          label='Buscar...'
          variant='outlined'
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder='Id del lobby'
          className='mb-4'
          onKeyPress={e => {
            if (e.key === 'Enter') {
              handleSearchLobby()
            }
          }}
        />
        {searchError && <div className='mb-4 text-red-500 dark:text-red-400 text-sm text-center'>{searchError}</div>}
        <div className='flex gap-3 mt-6'>
          <button
            className='bg-primary hover:bg-light_coral text-white text-center py-2 w-32 rounded-md transition-colors duration-200'
            onClick={handleSearchLobby}
          >
            Buscar
          </button>
          <button
            className='bg-gray hover:bg-light_gray dark:hover:bg-dk-hover text-white text-center py-2 w-32 rounded-md transition-colors duration-200'
            onClick={() => setOpenSearchModal(false)}
          >
            Cancelar
          </button>
        </div>
      </Modal>
    </div>
  )
}

const PreLobbyInfo = ({ lobbyId, setSelectedLobby }) => {
  const { loading, data } = useFetch(`${urlApi}/admin/lobbies/${lobbyId}`)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (!loading) setVisible(false)
  }, [loading])

  return (
    <>
      <Modal
        isOpen={visible}
        onRequestClose={() => setVisible(false)}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 50
          },
          content: {
            width: '50%',
            height: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 'auto',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: '#f8f8f8',
            fontSize: '1.5rem'
          }
        }}
      >
        Cargando...
      </Modal>
      {!loading && <LobbyInfo lobby={data} setSelected={setSelectedLobby} />}
    </>
  )
}

const SearchLobbyInfo = ({ lobbyId, setSelectedLobby, setSearchLobbyId }) => {
  const { loading, data, error } = useFetch(`${urlApi}/admin/lobbies/${lobbyId}`)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (!loading) {
      setVisible(false)
      if (data && !error) {
        setSelectedLobby(data)
      }
      // Limpiar el ID de búsqueda después de procesar
      setSearchLobbyId('')
    }
  }, [loading, data, error, setSelectedLobby, setSearchLobbyId])

  return (
    <>
      <Modal
        isOpen={visible}
        onRequestClose={() => {
          setVisible(false)
          setSearchLobbyId('')
        }}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 50
          },
          content: {
            width: '50%',
            height: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 'auto',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: '#f8f8f8',
            fontSize: '1.5rem'
          }
        }}
      >
        {loading ? 'Cargando...' : error ? 'No se encontró el lobby' : 'Lobby encontrado'}
      </Modal>
      {!loading && data && !error && <LobbyInfo lobby={data} setSelected={setSelectedLobby} />}
    </>
  )
}

export default Lobbies
